import { useState } from 'react';
import { Alert, Box, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import { useLogEvents } from '@/hooks/useLogEvents';
import type { ListLogEventsParams } from '@/hooks/useLogEvents';
import { formatRelative } from '@/utils/formatRelative';
import { levelChipColor } from '@/utils/levelChipColor';

const PAGE_SIZE = 50;

const columns: GridColDef[] = [
  {
    field: 'timestamp',
    headerName: 'Time',
    width: 160,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? formatRelative(params.value as string) : '—',
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 120,
  },
  {
    field: 'level',
    headerName: 'Level',
    width: 90,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        size="small"
        label={params.value as string}
        color={levelChipColor((params.value as string) ?? '')}
      />
    ),
  },
  {
    field: 'message',
    headerName: 'Message',
    flex: 2,
    minWidth: 320,
    renderCell: (params: GridRenderCellParams) => (
      <Box
        title={params.value as string}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {params.value as string}
      </Box>
    ),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 90,
  },
];

interface LogEventTableProps {
  filter?: Pick<ListLogEventsParams, 'service' | 'level' | 'since'>;
}

export function LogEventTable({ filter }: LogEventTableProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const { data, isLoading, error } = useLogEvents({
    ...filter,
    page: paginationModel.page + 1,
  });

  return (
    <Box>
      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Failed to load log events: {error.message}
        </Alert>
      )}

      <DataGrid
        rows={data?.results ?? []}
        columns={columns}
        loading={isLoading}
        density="compact"
        disableRowSelectionOnClick
        pagination
        paginationMode="server"
        rowCount={data?.count ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        initialState={{
          pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } },
        }}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>
  );
}
